import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { env } from "../config";
import axios from "axios";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  LinkedinIcon,
  WhatsappIcon,
} from "react-share";
import ArticleTranslateWidget from "../components/ArticleTranslateWidget";
import ArticleComments from "../components/ArticleComments";

const SingleEvent = () => {
  const [post, setPost] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const params = useParams();
  const { key } = params;
  const contentRef = useRef(null);

  useEffect(() => {
    if (!key) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${env.baseUrl}/api/details/${key}`)
      .then((response) => {
        if (response.data && response.data.title) {
          setPost(response.data);
          setLoading(false);
        } else {
          setNotFound(true);
          setLoading(false);
        }
      })
      .catch(() => {
        axios
          .get(`${env.baseUrl}/api/event/details/${key}`)
          .then((res) => {
            if (res.data && res.data.title) {
              setPost(res.data);
              setLoading(false);
            } else {
              setNotFound(true);
              setLoading(false);
            }
          })
          .catch(() => {
            setNotFound(true);
            setLoading(false);
          });
      });
  }, [key]);

  // Fallback for any broken inline images inside event content
  useEffect(() => {
    if (!contentRef.current) return;
    const handleImgError = (e) => {
      if (e.target.tagName === "IMG") {
        e.target.onerror = null;
        e.target.src = "/images/noimgpost.jpeg";
      }
    };
    const el = contentRef.current;
    el.addEventListener("error", handleImgError, true);
    return () => el.removeEventListener("error", handleImgError, true);
  }, [post.content]);

  const getReadMoreLink = () => {
    if (!post) return null;

    if (post.source && post.source.toLowerCase() === "eventbrite") {
      return post.evenbrite_link || null;
    }

    return post.source || null;
  };

  const readMoreLink = getReadMoreLink();

  if (loading) {
    return (
      <div className="container py-5 text-center" style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Loading event...</span>
        </div>
      </div>
    );
  }

  if (notFound || !post || !post.title) {
    return (
      <div className="container py-5 text-center" style={{ minHeight: "55vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: "550px", padding: "40px 20px" }}>
          <i className="fa-solid fa-calendar-xmark" style={{ fontSize: "3.5rem", color: "#dc3545", marginBottom: "20px" }}></i>
          <h2 style={{ fontWeight: "700", marginBottom: "12px", color: "#333" }}>Event Not Found</h2>
          <p style={{ color: "#6c757d", fontSize: "1.05rem", lineHeight: "1.6", marginBottom: "24px" }}>
            The event you are looking for does not exist, has concluded, or has been removed from our listings.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <a href="/agendas" className="btn btn-primary" style={{ padding: "10px 24px", borderRadius: "8px", fontWeight: "600" }}>
              Explore Agendas
            </a>
            <a href="/categories" className="btn btn-outline-secondary" style={{ padding: "10px 24px", borderRadius: "8px", fontWeight: "600" }}>
              Browse Categories
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="single-post">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="content-block">
                {post.image && (
                  <div className="feature-image mb-3">
                    <img
                      src={post.image}
                      alt={post.title}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/images/noimgpost.jpeg";
                      }}
                      style={{
                        width: "100%",
                        borderRadius: "12px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}

                <h1>{post.title}</h1>

                {readMoreLink && (
                  <p
                    className="text-muted mt-3"
                    style={{
                      fontSize: "0.92rem",
                      color: "#6c757d",
                      lineHeight: "1.7",
                    }}
                  >
                    <strong>Disclaimer:</strong> The content above has been sourced from
                    external or third-party platforms. All rights belong to their
                    respective owners. For the original version, please visit{" "}
                    <a href={readMoreLink} target="_blank" rel="noopener noreferrer">
                      this source link
                    </a>
                    .
                  </p>
                )}

                <div
                  ref={contentRef}
                  className="post-body mt-3"
                  translate="yes"
                  dangerouslySetInnerHTML={{ __html: post.content || "" }}
                />

                {readMoreLink && (
                  <div className="mt-4">
                    <a
                      href={readMoreLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Read More
                    </a>
                  </div>
                )}

                <div className="share-section mt-5">
                  <h4>Share this event:</h4>
                  <div
                    className="flex gap-2"
                    style={{
                      display: "flex",
                      gap: "12px",
                      flexWrap: "wrap",
                      marginTop: "8px",
                    }}
                  >
                    <FacebookShareButton url={window.location.href} quote={post.title}>
                      <FacebookIcon size={30} round />
                    </FacebookShareButton>
                    <TwitterShareButton url={window.location.href} title={post.title}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          background: "#000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <path d="M18.244 2H21.5l-7.19 8.22L22 22h-6.828l-5.346-7.021L3.66 22H.4l7.693-8.793L2 2h6.995l4.835 6.36L18.244 2zm-1.2 18h1.803L7.972 4H6.037l11.007 16z" />
                        </svg>
                      </div>
                    </TwitterShareButton>
                    <LinkedinShareButton url={window.location.href} title={post.title}>
                      <LinkedinIcon size={30} round />
                    </LinkedinShareButton>
                    <WhatsappShareButton url={window.location.href} title={post.title}>
                      <WhatsappIcon size={30} round />
                    </WhatsappShareButton>
                  </div>
                </div>

                <ArticleTranslateWidget pageLanguage="en" />
                <ArticleComments articleType="event" articleKey={key} title={post.title} />
              </div>
            </div>

            <div className="col-lg-4">
              <div className="sidebar">
                <div className="box">
                  <h3>Event Details</h3>

                  <div className="inner-block">
                    <div className="icon">
                      <i className="fa-solid fa-calendar-days"></i>
                    </div>
                    <div className="text">
                      Date <br />
                      {post.start_date && new Date(post.start_date).toLocaleDateString("en-GB")}
                    </div>
                  </div>

                  <div className="inner-block">
                    <div className="icon">
                      <i className="fa-regular fa-folder"></i>
                    </div>
                    <div className="text">
                      Category <br />
                      <ul style={{ paddingLeft: "15px", margin: 0 }}>
                        <li>{post.category_id || "Uncategorized"}</li>
                      </ul>
                    </div>
                  </div>

                  <h3 style={{ marginTop: "20px" }}>Share this event</h3>

                  <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                    <FacebookShareButton url={window.location.href} quote={post.title}>
                      <FacebookIcon size={30} round />
                    </FacebookShareButton>
                    <TwitterShareButton url={window.location.href} title={post.title}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          background: "#000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <path d="M18.244 2H21.5l-7.19 8.22L22 22h-6.828l-5.346-7.021L3.66 22H.4l7.693-8.793L2 2h6.995l4.835 6.36L18.244 2zm-1.2 18h1.803L7.972 4H6.037l11.007 16z" />
                        </svg>
                      </div>
                    </TwitterShareButton>
                    <LinkedinShareButton url={window.location.href} title={post.title}>
                      <LinkedinIcon size={30} round />
                    </LinkedinShareButton>
                    <WhatsappShareButton url={window.location.href} title={post.title}>
                      <WhatsappIcon size={30} round />
                    </WhatsappShareButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SingleEvent;
